import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { opcionesAPI, type Deposito, type Sucursal, depositoSucursalIds } from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";

function apiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const e = error as {
    message?: unknown;
    details?: unknown;
    data?: Record<string, unknown>;
  };
  if (typeof e.message === "string" && e.message.trim() && e.message !== "Bad Request") {
    return e.message;
  }
  const details = e.details ?? e.data;
  if (typeof details === "string" && details.trim()) return details;
  if (details && typeof details === "object") {
    const d = details as Record<string, unknown>;
    if (typeof d.detail === "string" && d.detail.trim()) return d.detail;
    for (const key of ["nombre", "non_field_errors", "detail"]) {
      const v = d[key];
      if (typeof v === "string" && v.trim()) return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
    for (const v of Object.values(d)) {
      if (typeof v === "string" && v.trim()) return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  return fallback;
}

interface FormData {
  codigo: string;
  nombre: string;
  sucursales: string[];
  direccion: string;
}

const emptyForm: FormData = {
  codigo: "",
  nombre: "",
  sucursales: [],
  direccion: ""
};

interface DepositosABMProps {
  selectedItem?: Deposito | null;
  onBack?: () => void;
  onSaved?: () => void;
}

export function DepositosABM({ selectedItem, onBack, onSaved }: DepositosABMProps) {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [originalFormData, setOriginalFormData] = useState<FormData>(emptyForm);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredDepositos, setFilteredDepositos] = useState<Deposito[]>([]);
  const [allDepositos, setAllDepositos] = useState<Deposito[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [deps, sucs] = await Promise.all([
          opcionesAPI.listDepositos(),
          opcionesAPI.listSucursales()
        ]);
        setAllDepositos(deps);
        setSucursales(sucs);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Error al cargar datos");
      }
    };
    loadData();
  }, []);

  // Load selectedItem when passed from parent
  useEffect(() => {
    if (selectedItem) {
      selectDeposito(selectedItem);
    } else {
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setHasChanges(false);
      setIsEditing(false);
    }
  }, [selectedItem]);

  const handleNombreChange = (value: string) => {
    setFormData({ ...formData, nombre: value });
    checkForChanges({ ...formData, nombre: value });

    if (value.trim()) {
      const filtered = allDepositos.filter(d =>
        d.nombre?.toLowerCase().includes(value.toLowerCase()) ||
        d.codigo?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDepositos(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectDeposito = (deposito: Deposito) => {
    const loadedData: FormData = {
      codigo: deposito.codigo,
      nombre: deposito.nombre,
      sucursales: depositoSucursalIds(deposito).map(String),
      direccion: deposito.direccion || ""
    };
    setFormData(loadedData);
    setOriginalFormData(loadedData);
    setHasChanges(false);
    setShowSuggestions(false);
    setIsEditing(true);
  };

  const handleNombreKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (filteredDepositos.length > 0) {
        selectDeposito(filteredDepositos[0]);
      } else if (formData.nombre.trim() && !isEditing) {
        setHasChanges(true);
        setShowSuggestions(false);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const checkForChanges = (data: FormData) => {
    const changed = JSON.stringify(data) !== JSON.stringify(originalFormData);
    setHasChanges(changed);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    checkForChanges(newData);
  };

  const toggleSucursal = (sucursalId: string, checked: boolean) => {
    const next = checked
      ? [...formData.sucursales, sucursalId]
      : formData.sucursales.filter((id) => id !== sucursalId);
    const unique = [...new Set(next)];
    const newData = { ...formData, sucursales: unique };
    setFormData(newData);
    checkForChanges(newData);
  };

  const goToListado = () => {
    setShowDeleteDialog(false);
    if (onBack) onBack();
    else if (onSaved) onSaved();
  };

  const handleSave = async () => {
    const nombreNorm = formData.nombre.trim();
    if (!nombreNorm) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formData.sucursales.length) {
      toast.error("Asigne al menos una sucursal");
      return;
    }

    setLoading(true);
    try {
      const sucursalIds = formData.sucursales.map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n));

      if (isEditing && formData.codigo) {
        await opcionesAPI.updateDeposito(formData.codigo, {
          nombre: nombreNorm,
          sucursales: sucursalIds,
          direccion: formData.direccion,
        });
        toast.success("Depósito actualizado");
      } else {
        // #12: depósitos pueden repetir nombre — no bloquear duplicados.
        await opcionesAPI.createDeposito({
          codigo: "",
          nombre: nombreNorm,
          sucursales: sucursalIds,
          direccion: formData.direccion,
          activo: true,
        });
        toast.success("Depósito creado");
      }

      dataRefresh.emit("depositos");
      goToListado();
    } catch (error: unknown) {
      console.error("Error saving deposito:", error);
      toast.error(apiErrorMessage(error, "Error al guardar depósito"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await opcionesAPI.deleteDeposito(formData.codigo);
      toast.success("Depósito eliminado");
      dataRefresh.emit("depositos");
      goToListado();
    } catch (error: unknown) {
      console.error("Error deleting deposito:", error);
      toast.error(apiErrorMessage(error, "Error al eliminar depósito"));
      setShowDeleteDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setOriginalFormData(emptyForm);
    setHasChanges(false);
    setIsEditing(false);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleNew}>
          Nuevo Depósito
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            className="w-full px-4 py-2 border border-input bg-muted rounded-lg focus:outline-none text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div className="relative">
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => handleNombreChange(e.target.value)}
            onKeyDown={handleNombreKeyDown}
            onFocus={() => {
              if (formData.nombre.trim() && filteredDepositos.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Escriba para buscar o crear..."
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          {showSuggestions && filteredDepositos.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredDepositos.map((deposito) => (
                <button
                  key={deposito.codigo}
                  type="button"
                  onClick={() => selectDeposito(deposito)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex justify-between items-center"
                >
                  <span>{deposito.nombre}</span>
                  <span className="text-xs text-muted-foreground">{deposito.codigo}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Sucursales</label>
          <div className="border border-input rounded-lg px-3 py-2 max-h-40 overflow-y-auto space-y-2 bg-input">
            {sucursales.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay sucursales cargadas</p>
            ) : (
              sucursales.map((suc) => {
                const sid = String(suc.id || suc.codigo);
                return (
                  <label
                    key={suc.codigo}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={formData.sucursales.includes(sid)}
                      onCheckedChange={(checked) =>
                        toggleSucursal(sid, checked === true)
                      }
                    />
                    <span>{suc.nombre}</span>
                  </label>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Un depósito puede servir a varias sucursales. El stock es un solo montón.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Dirección</label>
          <input
            type="text"
            value={formData.direccion}
            onChange={(e) => handleInputChange("direccion", e.target.value)}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
      </div>
      <FormFooter
        onBack={onBack}
        onDelete={handleDelete}
        deleteDisabled={!formData.codigo || loading}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saving={loading}
      />
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas eliminar este depósito?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}